import React, { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Pagination } from '~/Components';
import { Dots, Tab, Tabs, Typography, Table, TableRow, TableCell, TableBody } from '~/UIKit';
import { IDragHistoryItem } from '../../Hooks';
import { IErrorResponse } from '../../Models/DTO/Error.models';
import { Error } from '../Error';
import { FitoListContent } from './FitoListContent';
import { DataTableHeader } from '../DataTable/DataTableHeader';
import { EmptyStateView, IEmptyStateView } from '../EmptyStateView';
import { ListToolbar } from './ListToolbar';
import { IListAction } from './ListToolbar/ListAction.models';
import { IFitoListItem } from './models';
import { useFitoList } from './useFitoList';
import { FitoListStyles } from './FitoList.styles';
import { classnames } from 'tss-react/tools/classnames';
import { TableDefinition } from '../DataTable/DataTable.types';
import { SelectChangeEvent, Theme, useMediaQuery } from '@mui/material';

export interface IFitoListProps {
  items: IFitoListItem[];
  tabbedFilters?: string[];
  onTabbedFilterChange?: (index: number) => void;
  onSearch?: (text: string) => void;
  resetSearch?: boolean;
  onSelect?: (id: string | string[], checked?: boolean) => void;
  bulkActions?: IListAction[];
  Actions?: IListAction[];
  onRearrange?: (newList: IFitoListItem[]) => void;
  isLoading?: boolean;
  addToDnDHistory?: (history: IDragHistoryItem) => void;
  isBeingRearranged?: boolean;
  setIsBeingRearranged?: (isBeingRearranged: boolean) => void;
  error?: IErrorResponse;
  errorAction?: () => void;
  placeholder?: IEmptyStateView;
  pageSize?: number;
  page?: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  multiSelect?: boolean;
  isSelectPossible?: boolean;
  isSelectionActive?: boolean;
  size?: 'small';
  filterComponent?: React.ReactNode;
  desktopView?: {
    items: any[];
    tableDefinition: TableDefinition;
    onRowClicked?: (row: any) => void;
    onRowSelected?: (row: any) => void;
  };
  sortOptions?: { key: string; label: string }[];
  onSortSelect?: (event: SelectChangeEvent) => void;
  title?: string;
  hasNPlusOne?: boolean;
  pageStartValue?: number;
  searchTextPlaceholder?: string;
  total?: number;
  infiniteScroll?: boolean;
  moreActions?: JSX.Element;
}

export const FitoList = ({
  items,
  filterComponent,
  tabbedFilters,
  onTabbedFilterChange,
  onSearch,
  onSelect,
  bulkActions,
  Actions,
  onRearrange,
  isLoading,
  addToDnDHistory,
  isBeingRearranged,
  setIsBeingRearranged,
  error,
  errorAction,
  placeholder,
  pageSize = 10,
  page,
  onNextPage,
  onPreviousPage,
  multiSelect = false,
  isSelectPossible = true,
  isSelectionActive = false,
  hasNPlusOne = false,
  pageStartValue = 1,
  size,
  desktopView: _desktopView,
  resetSearch,
  sortOptions,
  onSortSelect,
  title,
  searchTextPlaceholder,
  total,
  infiniteScroll = false,
  moreActions,
}: IFitoListProps) => {
  const { classes } = FitoListStyles(pageSize, infiniteScroll)();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const {
    handleChange,
    onItemRearranged,
    onRearrangeAction,
    options,
    value,
    rearrangedList,
    onSelectAll,
  } = useFitoList({
    items,
    onRearrange,
    onTabbedFilterChange,
    setIsBeingRearranged,
    onSelect,
  });

  const desktopView = _desktopView
    ? {
        ..._desktopView,
        items: infiniteScroll ? _desktopView.items : _desktopView.items.slice(0, pageSize),
      }
    : undefined;

  useEffect(() => {
    // this is a known problem with tabs in popovers :(
    // https://github.com/mui-org/material-ui/issues/9337
    window.dispatchEvent(new CustomEvent('resize'));
  }, []);

  const fitoListItems = infiniteScroll ? rearrangedList : rearrangedList.slice(0, pageSize);

  return (
    <DndProvider backend={TouchBackend} options={options}>
      <div
        className={classnames([
          { [classes.FitoListContainer]: true, [classes.FitoListSmall]: size === 'small' },
        ])}
      >
        {size === 'small' && !isMobile && title && (
          <Typography className={classes.FitoListTitle}>{title}</Typography>
        )}
        {error && (
          <div className={classes.ErrorWrapper}>
            <Error error={error} errorAction={errorAction} />
          </div>
        )}
        {(tabbedFilters || filterComponent || bulkActions?.length || onSearch || multiSelect) && (
          <div className={classes.FitoListToolbarWrapper}>
            <ListToolbar
              items={items.slice(0, pageSize)}
              onSearch={onSearch}
              resetSearch={resetSearch}
              onSelectAll={onSelectAll}
              onRearrange={onRearrange && onRearrangeAction}
              bulkActions={bulkActions}
              actions={Actions}
              size={size}
              multiSelect={multiSelect}
              isSelectionActive={isSelectionActive}
              sortOptions={sortOptions}
              onSortSelect={onSortSelect}
              filterComponent={filterComponent}
              searchTextPlaceholder={searchTextPlaceholder}
              moreActions={moreActions}
            />
            {tabbedFilters && (
              <Tabs className={classes.tabsContainer} value={value} onChange={handleChange}>
                {tabbedFilters.map((tab, index) => (
                  <Tab label={tab} key={`tab_${tab}_${index}`} />
                ))}
              </Tabs>
            )}
          </div>
        )}
        <div className={classes.FitoListContentWrapper}>
          <Table size="small" stickyHeader className={classes.table}>
            {!isMobile && desktopView && <DataTableHeader desktopView={desktopView} />}
            {isLoading && !infiniteScroll ? (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={desktopView?.tableDefinition.columns.length}
                    className={classes.loading}
                  >
                    <Dots />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : rearrangedList?.length > 0 ? (
              <FitoListContent
                items={fitoListItems}
                isBeingRearranged={isBeingRearranged}
                onItemRearranged={onItemRearranged}
                addToDnDHistory={addToDnDHistory}
                onSelect={onSelect}
                multiSelect={multiSelect}
                isSelectionActive={isSelectionActive}
                desktopView={desktopView}
                isSelectPossible={isSelectPossible}
              />
            ) : (
              placeholder && (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={desktopView?.tableDefinition.columns.length}
                      className={classes.loading}
                    >
                      <EmptyStateView
                        imgSrc={placeholder.imgSrc}
                        imgAlt={placeholder.imgAlt}
                        title={placeholder.title}
                        description={placeholder.description}
                        action={{
                          name: placeholder.action?.name,
                          icon: placeholder.action?.icon,
                        }}
                        onAction={placeholder.onAction}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )
            )}
          </Table>
          {infiniteScroll && isLoading && <Dots />}
        </div>
        {page !== undefined && pageSize && onNextPage && onPreviousPage && (
          <Pagination
            page={page}
            pageSize={pageSize}
            currentListLength={rearrangedList.length}
            onNextPage={onNextPage}
            onPreviousPage={onPreviousPage}
            isDisabled={isLoading}
            hasNPlusOne={hasNPlusOne}
            pageStartValue={pageStartValue}
            total={total}
          />
        )}
      </div>
    </DndProvider>
  );
};
